import React from "react";
import { motion } from "framer-motion";
import { Zap, Clock, MapPin, Phone, ShieldCheck, ShoppingBag, Handshake, Coffee, Trophy, CircleDot, Snowflake, BatteryCharging, Target, MessageCircle, Bell } from "lucide-react";

const WANSPORT_URL = "https://wansport.com";

export const Marquee = () => {
  const items = ["Padel Elvo", "·", "15€ a persona", "·", "3 Campi Padel", "·", "Campo Pickleball", "·", "Spara Palline", "·", "Tornei", "·", "Bar & Aperitivi", "·", "Partner SANE", "·", "Occhieppo Superiore"];
  const content = [...items, ...items, ...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-[#0A0A0A] py-6" data-testid="marquee">
      <div className="marquee-track flex gap-8 whitespace-nowrap">
        {content.map((t, i) => (
          <span
            key={i}
            className={`font-oswald uppercase tracking-[0.25em] text-2xl md:text-4xl ${
              t === "·" ? "text-[#CCFF00]" : "text-white/90"
            }`}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

export const Services = ({ media }) => {
  const services = [
    {
      id: "campi",
      title: "3 Campi Padel",
      icon: Zap,
      desc:
        "Tre campi panoramici indoor di ultima generazione, illuminazione LED e atmosfera da club. Presto ci espanderemo con nuovi campi.",
      img: media.courts_overview,
      size: "col-span-12 lg:col-span-7 row-span-2",
      badge: "01",
      tag: "Padel",
    },
    {
      id: "pickleball",
      title: "Campo Pickleball",
      icon: CircleDot,
      desc:
        "Il primo campo indoor di pickleball della zona. Lo sport americano in crescita, ora anche a Occhieppo.",
      img: media.pickleball,
      size: "col-span-12 lg:col-span-5",
      badge: "02",
      tag: "Pickleball",
    },
    {
      id: "spogliatoi",
      title: "Spogliatoi & Docce",
      icon: ShieldCheck,
      desc:
        "Spogliatoi con armadietti e docce moderne. Arriva, gioca, rinfrescati.",
      img: media.locker_room,
      size: "col-span-12 lg:col-span-5",
      badge: "03",
      tag: "Club",
    },
  ];

  return (
    <section id="servizi" className="relative py-24 sm:py-32 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#CCFF00]" />
              <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
                Cosa Offriamo
              </span>
            </div>
            <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
              Tutto quello che<br />ti serve. In un posto.
            </h2>
          </div>
          <p className="max-w-md text-white/60 font-dm text-base md:text-lg">
            Un club pensato per giocatori veri. Tre campi indoor, Ghost Padel,
            maestri e spogliatoi moderni — tutto curato nei minimi dettagli.
          </p>
        </div>

        <div className="grid grid-cols-12 auto-rows-[280px] gap-4">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                className={`relative group overflow-hidden border border-white/10 bg-[#171717] ${s.size}`}
                data-testid={`service-card-${s.id}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
                  style={{ backgroundImage: `url(${s.img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
                <div className="absolute top-5 left-5 font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">
                  {s.badge} · {s.tag}
                </div>
                <div className="absolute top-5 right-5 w-10 h-10 border border-white/20 flex items-center justify-center backdrop-blur-md bg-black/30 transition-colors group-hover:border-[#CCFF00] group-hover:text-[#CCFF00]">
                  <Icon size={18} strokeWidth={2.25} />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h3 className="font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-white/70 leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Shop + Partner row */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="md:col-span-7 relative overflow-hidden border border-white/10 bg-[#171717] group aspect-[16/9] md:aspect-auto md:min-h-[340px]"
            data-testid="service-card-shop"
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
              style={{ backgroundImage: `url(${media.shop_sane})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/75 to-[#0A0A0A]/20" />
            <div className="relative h-full flex flex-col justify-between p-8 md:p-10">
              <div className="flex items-center gap-3">
                <ShoppingBag size={16} className="text-[#CCFF00]" strokeWidth={2.25} />
                <span className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">04 · Shop & Partner</span>
              </div>
              <div>
                <h3 className="font-oswald uppercase tracking-tight text-white text-3xl md:text-4xl leading-none">
                  Racchette SANE<br />in vendita al club
                </h3>
                <p className="mt-4 font-dm text-sm md:text-base text-white/70 max-w-md">
                  Collaboriamo con <span className="text-white font-medium">SANE — La Cuna del Pádel</span>.
                  Racchette, abbigliamento e accessori direttamente al club,
                  con il consiglio dei nostri maestri.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-5 relative overflow-hidden border border-white/10 bg-[#171717] group aspect-[16/9] md:aspect-auto md:min-h-[340px]"
            data-testid="service-card-sane"
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
              style={{ backgroundImage: `url(${media.sane_wall})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/20" />
            <div className="relative h-full flex flex-col justify-between p-8 md:p-10">
              <div className="flex items-center gap-3">
                <Handshake size={16} className="text-[#CCFF00]" strokeWidth={2.25} />
                <span className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">05 · Partnership</span>
              </div>
              <div>
                <h3 className="font-oswald uppercase tracking-tight text-white text-3xl md:text-4xl leading-none">
                  Official partner<br />SANE Padel
                </h3>
                <p className="mt-4 font-dm text-sm md:text-base text-white/80 max-w-md">
                  <span className="text-white font-medium">La Cuna del Pádel</span> dal 1989.
                  Prova le racchette in campo prima di acquistarle, con la
                  consulenza dei nostri maestri.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Amenities strip */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="border border-white/10 bg-[#171717] p-6 md:p-7 flex items-start gap-4 hover:border-[#CCFF00]/40 transition-colors"
            data-testid="amenity-climate"
          >
            <div className="w-11 h-11 border border-[#CCFF00]/40 bg-[#CCFF00]/5 flex items-center justify-center shrink-0">
              <Snowflake size={18} className="text-[#CCFF00]" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">Comfort</div>
              <h4 className="mt-1 font-oswald uppercase tracking-tight text-white text-lg md:text-xl leading-none">
                Riscaldamento & Aria Condizionata
              </h4>
              <p className="mt-2 font-dm text-sm text-white/60 max-w-md">
                Climatizzazione presente tutto l'anno: in inverno il club è caldo,
                in estate fresco. Si gioca sempre alla temperatura giusta.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="border border-white/10 bg-[#171717] p-6 md:p-7 flex items-start gap-4 hover:border-[#CCFF00]/40 transition-colors"
            data-testid="amenity-parking"
          >
            <div className="w-11 h-11 border border-[#CCFF00]/40 bg-[#CCFF00]/5 flex items-center justify-center shrink-0">
              <BatteryCharging size={18} className="text-[#CCFF00]" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">In Loco</div>
              <h4 className="mt-1 font-oswald uppercase tracking-tight text-white text-lg md:text-xl leading-none">
                Ricarica 22kW & Parcheggi
              </h4>
              <p className="mt-2 font-dm text-sm text-white/60 max-w-md">
                Parcheggi ampi sempre disponibili e colonnina di ricarica
                <span className="text-white font-medium"> 22kW</span> per auto
                elettriche: arrivi, giochi, riparti con la macchina carica.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const BarLife = ({ media }) => {
  return (
    <section id="bar" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10" data-testid="bar-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Coffee size={16} className="text-[#CCFF00]" />
              <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
                Bar & Aperitivo
              </span>
            </div>
            <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
              Dopo il match,<br />si beve qualcosa.
            </h2>
          </div>
          <p className="max-w-md text-white/60 font-dm text-base md:text-lg">
            Caffè, aperitivi, taglieri di salumi e formaggi locali e birra artigianale
            <span className="text-white font-medium"> 1851 alla spina</span>. Il nostro bar è il posto
            dove il club si incontra prima e dopo il gioco.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-7 relative overflow-hidden border border-white/10 bg-[#171717] aspect-[16/10] group"
            data-testid="bar-main-card"
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
              style={{ backgroundImage: `url(${media.bar_counter})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
            <div className="absolute top-5 left-5 font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">
              Il Nostro Bar
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h3 className="font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">
                Caffè · Aperitivi · Gluten Free
              </h3>
              <p className="mt-3 text-sm md:text-base text-white/70 max-w-md">
                Colazione, pausa post-allenamento o aperitivo con gli amici.
                Proposte anche gluten free per tutti.
              </p>
            </div>
          </motion.div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative overflow-hidden border border-white/10 bg-[#171717] group aspect-[4/3] lg:aspect-auto lg:min-h-[220px]"
              data-testid="bar-aperitivo-card"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                style={{ backgroundImage: `url(${media.bar_aperitivo})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00] mb-2">
                  Taglieri
                </div>
                <h3 className="font-oswald uppercase tracking-tight text-white text-xl md:text-2xl leading-none">
                  Salumi & Formaggi Locali
                </h3>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative overflow-hidden border border-white/10 bg-[#171717] group aspect-[4/3] lg:aspect-auto lg:min-h-[220px]"
              data-testid="bar-beers-card"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                style={{ backgroundImage: `url(${media.bar_beers})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00] mb-2">
                  Alla Spina
                </div>
                <h3 className="font-oswald uppercase tracking-tight text-white text-xl md:text-2xl leading-none">
                  Birra Artigianale 1851
                </h3>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Tornei = ({ media }) => {
  return (
    <section id="tornei" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10 overflow-hidden" data-testid="tornei-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-6 relative aspect-[4/3] border border-white/10 overflow-hidden group"
          data-testid="tornei-image"
        >
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{ backgroundImage: `url(${media.match_real})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#0A0A0A]/20 to-[#0A0A0A]/80" />
          <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
            <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">
              Competizione
            </div>
            <div className="w-12 h-12 border border-[#CCFF00]/50 bg-[#CCFF00]/5 flex items-center justify-center backdrop-blur-sm">
              <Trophy size={20} className="text-[#CCFF00]" strokeWidth={2.25} />
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#CCFF00]" />
              <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
                Tornei & Eventi
              </span>
            </div>
            <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
              Entra in<br />competizione.
            </h2>
            <p className="mt-6 text-white/60 font-dm text-base md:text-lg max-w-md">
              Organizziamo tornei, sfide a squadre e serate tematiche per ogni livello.
              Dal principiante curioso al giocatore più competitivo, c'è sempre qualcosa
              in programma a Padel Elvo.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <div className="border-l-2 border-[#CCFF00] pl-3" data-testid="tornei-stat-1">
                <div className="font-oswald text-white text-2xl md:text-3xl leading-none">Tornei</div>
                <div className="font-dm text-xs text-white/50 mt-1">tutti i livelli</div>
              </div>
              <div className="border-l-2 border-[#CCFF00] pl-3" data-testid="tornei-stat-2">
                <div className="font-oswald text-white text-2xl md:text-3xl leading-none">Sfide</div>
                <div className="font-dm text-xs text-white/50 mt-1">a squadre</div>
              </div>
              <div className="border-l-2 border-[#CCFF00] pl-3" data-testid="tornei-stat-3">
                <div className="font-oswald text-white text-2xl md:text-3xl leading-none">Eventi</div>
                <div className="font-dm text-xs text-white/50 mt-1">a tema</div>
              </div>
            </div>
            <a
              href="#torneo-privato"
              className="mt-10 btn-ghost px-7 py-4 text-sm inline-flex items-center gap-3"
              data-testid="tornei-info-button"
            >
              Richiedi un torneo privato
              <span aria-hidden>→</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};


export const Sparapalline = ({ media }) => {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Try to autoplay (muted to satisfy browser policy)
    video.muted = true;
    video.play().catch(() => {});

    // Try to unmute on first user interaction anywhere on the page
    const unmute = () => {
      try {
        video.muted = false;
        video.volume = 0.005;
        const p = video.play();
        if (p && typeof p.then === "function") p.catch(() => {});
      } catch (e) {
        // ignore
      }
    };
    const events = ["click", "touchstart", "keydown", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, unmute, { once: true, passive: true }));

    // Force the video to keep playing and never stop
    const onPause = () => {
      const p = video.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    };
    const onEnded = () => {
      video.currentTime = 0;
      onPause();
    };
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, unmute));
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  return (
    <section id="sparapalline" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10 overflow-hidden" data-testid="sparapalline-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-6">
            <Target size={16} className="text-[#CCFF00]" />
            <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
              Allenamento Solo
            </span>
          </div>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
            Spara Palline<br />professionale.
          </h2>
          <p className="mt-6 text-white/60 font-dm text-base md:text-lg max-w-md">
            Allena diritto, rovescio e risposta con la nostra macchina spara palline.
            Velocità, frequenza e angolo regolabili: programmi su misura per ogni
            livello, dal principiante al competitivo.
          </p>
          <ul className="mt-8 space-y-3 font-dm text-sm text-white/70">
            <li className="flex items-center gap-3" data-testid="spara-perk-1">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Disponibile su prenotazione
            </li>
            <li className="flex items-center gap-3" data-testid="spara-perk-2">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Anche in abbinata alla lezione
            </li>
            <li className="flex items-center gap-3" data-testid="spara-perk-3">
              <span className="w-1.5 h-1.5 bg-[#CCFF00]" /> Perfetta per allenarsi da soli
            </li>
          </ul>
          <a
            href="tel:+393336043477"
            className="mt-10 btn-ghost px-7 py-4 text-sm inline-flex items-center gap-3"
            data-testid="sparapalline-info-button"
          >
            Chiamaci per prenotare
            <span aria-hidden>→</span>
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 order-1 lg:order-2 relative aspect-[4/5] md:aspect-[4/3] lg:aspect-square border border-white/10 overflow-hidden bg-black"
          data-testid="sparapalline-video"
        >
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            poster={media.match_real}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="/sparapalline.mp4" type="video/mp4" />
            <source src="/sparapalline.mov" type="video/quicktime" />
          </video>
          <div className="absolute top-5 left-5 font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00] bg-black/50 px-3 py-1.5 backdrop-blur-sm pointer-events-none">
            Spara Palline · in azione
          </div>
          <div className="absolute top-5 right-5 w-10 h-10 border border-[#CCFF00]/50 bg-[#CCFF00]/5 flex items-center justify-center backdrop-blur-sm pointer-events-none">
            <Target size={18} className="text-[#CCFF00]" strokeWidth={2.25} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};


export const WhatsApp = () => {
  const groupUrl = "https://chat.whatsapp.com/GlDmui6IBjaDzIlY2I96HC";
  const channelUrl = "#";
  return (
    <section id="whatsapp" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10 overflow-hidden" data-testid="whatsapp-section">
      <div className="absolute -top-20 -left-32 w-[480px] h-[480px] rounded-full bg-[#25D366] opacity-[0.05] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle size={16} className="text-[#CCFF00]" />
              <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
                Resta Connesso
              </span>
            </div>
            <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
              Unisciti alla<br />community su WhatsApp.
            </h2>
          </div>
          <p className="max-w-md text-white/60 font-dm text-base md:text-lg">
            Trova partite all'ultimo momento, organizza tornei tra amici e ricevi
            tutti gli aggiornamenti del club in tempo reale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.a
            href={groupUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="group relative border border-white/10 bg-[#171717] p-8 md:p-10 overflow-hidden hover:border-[#25D366]/50 transition-colors"
            data-testid="whatsapp-group-card"
          >
            <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-[#25D366] opacity-[0.08] blur-3xl transition-opacity group-hover:opacity-[0.18]" />
            <div className="relative flex items-start justify-between gap-6">
              <div>
                <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">Gruppo Partite</div>
                <h3 className="mt-3 font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">
                  Trova compagni<br />e prenota partite
                </h3>
                <p className="mt-4 font-dm text-sm md:text-base text-white/70 max-w-md">
                  Cerchi un quarto? Vuoi proporre una partita? Entra nel gruppo
                  ufficiale dei giocatori di Padel Elvo.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-xs text-[#25D366]">
                  Entra nel gruppo →
                </div>
              </div>
              <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shrink-0 transition-transform group-hover:scale-110">
                <MessageCircle size={24} className="text-black" strokeWidth={2.5} />
              </div>
            </div>
          </motion.a>

          <motion.a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="group relative border border-white/10 bg-[#171717] p-8 md:p-10 overflow-hidden hover:border-[#25D366]/50 transition-colors"
            data-testid="whatsapp-channel-card"
          >
            <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-[#CCFF00] opacity-[0.05] blur-3xl transition-opacity group-hover:opacity-[0.12]" />
            <div className="relative flex items-start justify-between gap-6">
              <div>
                <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-[#CCFF00]">Canale Eventi</div>
                <h3 className="mt-3 font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-none">
                  Tornei & news<br />in tempo reale
                </h3>
                <p className="mt-4 font-dm text-sm md:text-base text-white/70 max-w-md">
                  Iscriviti al canale per non perdere tornei, eventi tematici,
                  promozioni e novità del club. Solo annunci, niente spam.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-xs text-white/50">
                  Link presto disponibile
                </div>
              </div>
              <div className="w-14 h-14 rounded-full border-2 border-[#CCFF00] flex items-center justify-center shrink-0 transition-transform group-hover:scale-110">
                <Bell size={22} className="text-[#CCFF00]" strokeWidth={2.5} />
              </div>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};


export const Pricing = () => {
  const features = [
    "Campi indoor illuminati",
    "Partita da 1h 30min",
    "Spara palline disponibile",
    "Riscaldamento & A/C",
  ];
  return (
    <section id="prezzi" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#CCFF00]" />
            <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
              Semplice. Chiaro.
            </span>
          </div>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
            15€ a testa.<br />Un'ora e mezza di gioco.
          </h2>
          <p className="mt-6 text-white/60 font-dm text-base md:text-lg max-w-md">
            Una tariffa trasparente per giocatore. Chiama gli amici, prenota
            il campo su Wansport e scendi in campo.
          </p>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative border border-white/15 bg-gradient-to-br from-[#171717] to-[#0A0A0A] p-8 md:p-12 overflow-hidden"
            data-testid="pricing-card"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#CCFF00] opacity-[0.08] blur-3xl" />
            <div className="flex items-start justify-between flex-wrap gap-6">
              <div>
                <div className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
                  Tariffa a Giocatore
                </div>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="font-oswald font-bold text-white text-7xl md:text-8xl leading-none">15€</span>
                  <span className="font-dm text-white/50 text-lg">/ persona</span>
                </div>
                <div className="mt-2 text-white/50 text-sm font-dm">
                  Partita da 1h 30min · Campo condiviso fino a 4 giocatori
                </div>
              </div>

              <a
                href={WANSPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-7 py-4 text-sm inline-flex items-center gap-2"
                data-testid="pricing-prenota-button"
              >
                Prenota Campo
                <span aria-hidden>→</span>
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-white/80 font-dm text-sm" data-testid={`pricing-feature-${i}`}>
                  <span className="w-1.5 h-1.5 bg-[#CCFF00]" />
                  {f}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Hours = () => {
  const days = [
    { d: "Lunedì", h: "11:00 – 23:00" },
    { d: "Martedì", h: "11:00 – 23:00" },
    { d: "Mercoledì", h: "11:00 – 23:00" },
    { d: "Giovedì", h: "11:00 – 23:00" },
    { d: "Venerdì", h: "11:00 – 23:00" },
    { d: "Sabato", h: "11:00 – 23:00" },
    { d: "Domenica", h: "11:00 – 23:00" },
  ];

  return (
    <section id="orari" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10" data-testid="hours-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <Clock size={16} className="text-[#CCFF00]" />
            <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
              Sempre Aperti
            </span>
          </div>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-5xl leading-[0.95]">
            Orari di<br />Apertura
          </h2>
          <p className="mt-6 text-white/60 font-dm text-base max-w-sm">
            Dal lunedì alla domenica, ogni giorno dalle 11:00 alle 23:00.
            <span className="text-white/40"> L'orario può variare nei giorni festivi.</span>
          </p>
        </div>

        <div className="lg:col-span-8">
          <div className="divide-y divide-white/10 border-y border-white/10">
            {days.map((day, i) => (
              <motion.div
                key={day.d}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="flex items-center justify-between py-5 group"
                data-testid={`hours-row-${day.d.toLowerCase()}`}
              >
                <div className="flex items-center gap-6">
                  <span className="font-oswald uppercase tracking-[0.25em] text-xs text-white/40 w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl">
                    {day.d}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-dm text-white/70 text-base md:text-lg">{day.h}</span>
                  <span className="hidden md:inline font-oswald uppercase tracking-[0.2em] text-[10px] text-[#CCFF00] opacity-0 group-hover:opacity-100 transition-opacity">
                    Aperto
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Location = () => {
  const mapSrc =
    "https://www.google.com/maps?q=Via%20Solata%2C%2013898%20Occhieppo%20Superiore%20BI&output=embed";
  return (
    <section id="contatti" className="relative py-24 sm:py-32 bg-[#0A0A0A] border-t border-white/10" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#CCFF00]" />
            <span className="font-oswald uppercase tracking-[0.3em] text-xs text-[#CCFF00]">
              Dove Trovarci
            </span>
          </div>
          <h2 className="font-oswald font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
            Vieni a giocare<br />a Occhieppo.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="border border-white/10 bg-[#171717] p-8 hover:border-[#CCFF00]/40 transition-colors" data-testid="contact-address">
              <div className="flex items-start gap-4">
                <MapPin size={22} className="text-[#CCFF00] mt-1 flex-shrink-0" />
                <div>
                  <div className="font-oswald uppercase tracking-[0.25em] text-[11px] text-white/50">Indirizzo</div>
                  <div className="mt-2 font-oswald uppercase tracking-tight text-white text-xl md:text-2xl leading-tight">
                    Via Solata<br />13898 Occhieppo Superiore, BI
                  </div>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=Via%20Solata%2C%2013898%20Occhieppo%20Superiore%20BI"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-[#CCFF00] font-oswald uppercase tracking-[0.2em] text-xs link-underline"
                    data-testid="contact-directions-link"
                  >
                    Indicazioni stradali →
                  </a>
                </div>
              </div>
            </div>

            <div className="border border-white/10 bg-[#171717] p-8 hover:border-[#CCFF00]/40 transition-colors" data-testid="contact-phone">
              <div className="flex items-start gap-4">
                <Phone size={22} className="text-[#CCFF00] mt-1 flex-shrink-0" />
                <div>
                  <div className="font-oswald uppercase tracking-[0.25em] text-[11px] text-white/50">Telefono</div>
                  <a
                    href="tel:+393336043477"
                    className="mt-2 block font-oswald uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight link-underline"
                    data-testid="contact-phone-link"
                  >
                    333 604 3477
                  </a>
                  <p className="mt-3 font-dm text-sm text-white/60">
                    Chiamaci per info, lezioni private o disponibilità dello spara palline.
                  </p>
                </div>
              </div>
            </div>

            <a
              href={WANSPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center px-7 py-5 text-base inline-flex items-center gap-3"
              data-testid="location-prenota-button"
            >
              Prenota su Wansport
              <span aria-hidden>→</span>
            </a>
          </div>

          <div className="lg:col-span-7 relative border border-white/10 overflow-hidden min-h-[440px]">
            <iframe
              title="Mappa Padel Elvo"
              src={mapSrc}
              className="absolute inset-0 w-full h-full"
              style={{ filter: "invert(92%) hue-rotate(180deg) saturate(0.7) contrast(0.95)" }}
              loading="lazy"
              data-testid="map-embed"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 bg-[#0A0A0A] py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_elvo-tennis-padel/artifacts/6mal192o_elvopadel.jpg"
                alt="Padel Elvo"
                className="w-12 h-12 rounded-full object-cover bg-white"
              />
              <div className="leading-tight">
                <div className="font-oswald uppercase tracking-[0.2em] text-sm text-white">Padel</div>
                <div className="font-oswald uppercase tracking-[0.2em] text-sm text-[#CCFF00] -mt-1">Elvo</div>
              </div>
            </div>
            <p className="mt-6 max-w-sm text-white/50 font-dm text-sm leading-relaxed">
              Padel Elvo — Il club di padel nel cuore del Biellese.
              Campi, bar, spara palline, maestri. Dal 2024.
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="font-oswald uppercase tracking-[0.25em] text-xs text-[#CCFF00] mb-4">Contatti</div>
            <ul className="space-y-2 font-dm text-sm text-white/70">
              <li>Via Solata</li>
              <li>13898 Occhieppo Superiore, BI</li>
              <li><a href="tel:+393336043477" className="link-underline">333 604 3477</a></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="font-oswald uppercase tracking-[0.25em] text-xs text-[#CCFF00] mb-4">Link</div>
            <ul className="space-y-2 font-dm text-sm text-white/70">
              <li><a href="#servizi" className="link-underline">Servizi</a></li>
              <li><a href="#prezzi" className="link-underline">Prezzi</a></li>
              <li><a href="#orari" className="link-underline">Orari</a></li>
              <li><a href="#contatti" className="link-underline">Contatti</a></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="font-oswald uppercase tracking-[0.25em] text-xs text-[#CCFF00] mb-4">Prenota</div>
            <a
              href={WANSPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-dm text-sm text-white hover:text-[#CCFF00] transition-colors link-underline"
              data-testid="footer-wansport-link"
            >
              App Wansport →
            </a>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-dm text-xs text-white/40">
            © {new Date().getFullYear()} Padel Elvo. Tutti i diritti riservati.
          </div>
          <div className="font-oswald uppercase tracking-[0.3em] text-[10px] text-white/30">
            Gioca · Sfida · Vinci
          </div>
        </div>
      </div>

      {/* Credit banner — studiobiellaweb.it */}
      <div className="mt-12 border-t border-white/10 bg-black">
        <a
          href="https://studiobiellaweb.it"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-5 px-6 lg:px-8 group transition-colors hover:bg-[#CCFF00]/5"
          data-testid="studiobiellaweb-credit"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <span className="font-oswald uppercase tracking-[0.3em] text-[11px] text-white/50">
              Sito creato da
            </span>
            <span className="font-oswald uppercase tracking-[0.2em] text-base sm:text-lg text-white group-hover:text-[#CCFF00] transition-colors">
              studiobiellaweb<span className="text-[#CCFF00]">.it</span>
            </span>
            <span className="hidden sm:inline-block font-oswald uppercase tracking-[0.3em] text-[11px] text-[#CCFF00] opacity-0 group-hover:opacity-100 transition-opacity">
              Visita →
            </span>
          </div>
        </a>
      </div>
    </footer>
  );
};
